import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Section, Card } from "@/components/Section";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { submitQuote } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

const SERVICE_OPTIONS = [
  "AI Consulting",
  "Leadership Consulting",
  "Training or Workshop",
  "Keynote or Conference Presentation",
  "PowerPoint Presentation",
  "Pitch Deck",
  "Website Design and Development",
  "AI Agent or Super Agent",
  "Workflow Design or Automation",
  "Research and Executive Brief",
  "Leadership Toolkit",
  "Other Custom Project",
];

const CONTACTS = ["Email", "Phone", "Either"];
const START_URGENCY = ["Immediately", "Within 2 weeks", "Within 1 month", "1–3 months", "Flexible"];
const HAS_CONTENT = ["I already have content and materials", "I have partial content", "Please write/develop content for me"];
const BUDGETS = ["Prefer to discuss with LAYOR"];

const initial = {
  first_name: "", last_name: "", company: "", job_title: "",
  email: "", phone: "", preferred_contact_method: "",
  service_needed: "", project_description: "", primary_goal: "",
  intended_audience: "", desired_completion_date: "", budget_range: "",
  start_urgency: "", has_content: "", additional_notes: "",
  service_details: {}, consent: false,
};

const getInitialService = () => {
  if (typeof window === "undefined") return "";
  const s = new URLSearchParams(window.location.search).get("service");
  return s || "";
};

export default function Quote() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(() => ({ ...initial, service_needed: getInitialService() }));
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const s = params.get("service");
    if (s) setForm((f) => (f.service_needed === s ? f : { ...f, service_needed: s }));
  }, [params]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const setDetail = (k) => (v) => setForm((f) => ({ ...f, service_details: { ...f.service_details, [k]: v?.target ? v.target.value : v } }));

  const isPresentation = useMemo(() => ["PowerPoint Presentation"].includes(form.service_needed), [form.service_needed]);
  const isPitchDeck = useMemo(() => form.service_needed === "Pitch Deck", [form.service_needed]);
  const isWebsite = useMemo(() => form.service_needed === "Website Design and Development", [form.service_needed]);
  const isTraining = useMemo(() => ["Training or Workshop", "Keynote or Conference Presentation"].includes(form.service_needed), [form.service_needed]);
  const isAgent = useMemo(() => form.service_needed === "AI Agent or Super Agent", [form.service_needed]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please provide consent to submit this form."); return; }
    if (!form.service_needed) { toast.error("Please select a service."); return; }
    setSubmitting(true);
    try {
      await submitQuote(form);
      setSubmitted(true);
      toast.success("Quote request received. We will be in touch.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="page-quote">
      <Section eyebrow="Request a Quote" title="Tell us what you need."
        kicker="Pricing is customized based on the scope and needs of each engagement. Share your goals, audience, timeline, and desired outcome, and a member of the LAYOR Digital Group team will contact you to discuss your priorities and the appropriate options.">
        {submitted ? (
          <Card data-testid="quote-success">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[var(--layor-gold)]/20 text-[var(--layor-navy)]"><CheckCircle2 size={22} /></div>
            <h3 className="mt-4 font-display text-2xl text-[var(--layor-navy)]">Thank you for contacting LAYOR Digital Group.</h3>
            <p className="mt-3 text-[var(--layor-muted)] leading-relaxed max-w-2xl">
              We have received your request and will review the information you provided. A member of our team will contact you to discuss your goals, answer any questions, and determine the appropriate next steps for your project.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/" className="btn-primary rounded-full px-5 py-2.5 text-sm font-semibold">Back to Home</Link>
              <Link to="/services" className="btn-ghost rounded-full px-5 py-2.5 text-sm font-semibold">Explore Services</Link>
            </div>
          </Card>
        ) : (
          <Card>
            <p className="text-sm text-[var(--layor-muted)]">Quotes are customized based on scope, timeline, content, design, and level of support. Prices are not displayed automatically.</p>
            <p className="mt-2 text-xs text-[var(--layor-navy)] font-semibold">Please note: Prices may vary at any time. Every quote is valid for 14 days from the date of issue.</p>
            <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-5 mt-6" data-testid="quote-form">
              <Field label="First name *"><Input required data-testid="quote-first-name" value={form.first_name} onChange={set("first_name")} /></Field>
              <Field label="Last name *"><Input required data-testid="quote-last-name" value={form.last_name} onChange={set("last_name")} /></Field>
              <Field label="Company or organization"><Input data-testid="quote-company" value={form.company} onChange={set("company")} /></Field>
              <Field label="Job title or role"><Input data-testid="quote-title" value={form.job_title} onChange={set("job_title")} /></Field>
              <Field label="Email *"><Input type="email" required data-testid="quote-email" value={form.email} onChange={set("email")} /></Field>
              <Field label="Telephone number"><Input data-testid="quote-phone" value={form.phone} onChange={set("phone")} /></Field>
              <Field label="Preferred contact method">
                <Select value={form.preferred_contact_method} onValueChange={set("preferred_contact_method")}>
                  <SelectTrigger data-testid="quote-contact-method"><SelectValue placeholder="Select preference" /></SelectTrigger>
                  <SelectContent>{CONTACTS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Service needed *">
                <Select value={form.service_needed} onValueChange={set("service_needed")}>
                  <SelectTrigger data-testid="quote-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
                  <SelectContent>{SERVICE_OPTIONS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>

              <Field label="Description of the project *" full>
                <Textarea required rows={4} data-testid="quote-description" value={form.project_description} onChange={set("project_description")} />
              </Field>
              <Field label="Primary goal" full>
                <Textarea rows={3} data-testid="quote-goal" value={form.primary_goal} onChange={set("primary_goal")} />
              </Field>
              <Field label="Intended audience" full>
                <Textarea rows={2} data-testid="quote-audience" value={form.intended_audience} onChange={set("intended_audience")} />
              </Field>

              <Field label="Desired completion date"><Input type="date" data-testid="quote-completion" value={form.desired_completion_date} onChange={set("desired_completion_date")} /></Field>
              <Field label="How soon should the project begin?">
                <Select value={form.start_urgency} onValueChange={set("start_urgency")}>
                  <SelectTrigger data-testid="quote-urgency"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{START_URGENCY.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Existing content">
                <Select value={form.has_content} onValueChange={set("has_content")}>
                  <SelectTrigger data-testid="quote-content"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{HAS_CONTENT.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Investment expectations (optional)">
                <Select value={form.budget_range} onValueChange={set("budget_range")}>
                  <SelectTrigger data-testid="quote-budget"><SelectValue placeholder="Prefer to discuss with LAYOR" /></SelectTrigger>
                  <SelectContent>{BUDGETS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>

              {/* Conditional service-specific intake */}
              {isPresentation && <PresentationIntake set={setDetail} data={form.service_details} />}
              {isPitchDeck && <PitchDeckIntake set={setDetail} data={form.service_details} />}
              {isWebsite && <WebsiteIntake set={setDetail} data={form.service_details} />}
              {isTraining && <TrainingIntake set={setDetail} data={form.service_details} />}
              {isAgent && <AgentIntake set={setDetail} data={form.service_details} />}

              <Field label="Additional questions or instructions" full>
                <Textarea rows={3} data-testid="quote-notes" value={form.additional_notes} onChange={set("additional_notes")} />
              </Field>

              <div className="sm:col-span-2 flex items-start gap-3 pt-2">
                <Checkbox id="quote-consent" checked={form.consent} onCheckedChange={(v) => setForm((f) => ({ ...f, consent: !!v }))} data-testid="quote-consent" />
                <Label htmlFor="quote-consent" className="text-sm text-[var(--layor-muted)] leading-relaxed">
                  I agree to be contacted about my project and consent to my information being stored securely by LAYOR Digital Group.
                </Label>
              </div>

              <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
                <button type="submit" disabled={submitting} className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-60" data-testid="quote-submit">
                  {submitting ? "Sending..." : "Submit Quote Request"}
                </button>
                <Link to="/contact" className="btn-ghost rounded-full px-5 py-3 text-sm font-semibold">Book a Consultation Instead</Link>
              </div>
            </form>
          </Card>
        )}
      </Section>
    </div>
  );
}

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label className="text-xs uppercase tracking-[0.18em] text-[var(--layor-muted)]">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);

const IntakeGroup = ({ title, children }) => (
  <div className="sm:col-span-2 mt-2 border-t border-[var(--layor-border)] pt-6">
    <div className="eyebrow text-[var(--layor-blue)]">{title}</div>
    <div className="mt-4 grid sm:grid-cols-2 gap-5">{children}</div>
  </div>
);

const PresentationIntake = ({ set, data }) => (
  <IntakeGroup title="Presentation Intake">
    <Field label="What is the presentation about?" full><Textarea rows={2} value={data.about || ""} onChange={set("about")} data-testid="pres-about" /></Field>
    <Field label="Purpose of the presentation"><Input value={data.purpose || ""} onChange={set("purpose")} data-testid="pres-purpose" /></Field>
    <Field label="Audience"><Input value={data.audience || ""} onChange={set("audience")} data-testid="pres-audience" /></Field>
    <Field label="Approx. audience size"><Input value={data.audience_size || ""} onChange={set("audience_size")} data-testid="pres-audience-size" /></Field>
    <Field label="Audience familiarity with subject"><Input value={data.familiarity || ""} onChange={set("familiarity")} data-testid="pres-familiarity" /></Field>
    <Field label="Event type (conference, board, workshop, etc.)"><Input value={data.event_type || ""} onChange={set("event_type")} /></Field>
    <Field label="Event date"><Input type="date" value={data.event_date || ""} onChange={set("event_date")} /></Field>
    <Field label="Presentation length (minutes)"><Input value={data.length || ""} onChange={set("length")} /></Field>
    <Field label="Preferred number of slides"><Input value={data.slide_count || ""} onChange={set("slide_count")} /></Field>
    <Field label="Required file format (PPT, Google Slides, PDF)"><Input value={data.file_format || ""} onChange={set("file_format")} /></Field>
    <Field label="Speaker notes / handouts / facilitator guide needed?" full><Textarea rows={2} value={data.extras || ""} onChange={set("extras")} /></Field>
    <Field label="Existing brand guide, logo, or template?" full><Textarea rows={2} value={data.brand || ""} onChange={set("brand")} /></Field>
    <Field label="Confidentiality requirements?" full><Textarea rows={2} value={data.confidentiality || ""} onChange={set("confidentiality")} /></Field>
  </IntakeGroup>
);

const PitchDeckIntake = ({ set, data }) => (
  <IntakeGroup title="Pitch Deck Intake">
    <Field label="Business, organization, or opportunity" full><Textarea rows={2} value={data.opportunity || ""} onChange={set("opportunity")} data-testid="pitch-opportunity" /></Field>
    <Field label="Audience (investors, lenders, partners, etc.)"><Input value={data.audience || ""} onChange={set("audience")} /></Field>
    <Field label="Primary goal of the pitch"><Input value={data.goal || ""} onChange={set("goal")} /></Field>
    <Field label="Specific action the audience should take" full><Textarea rows={2} value={data.action || ""} onChange={set("action")} /></Field>
    <Field label="Problem or need being addressed" full><Textarea rows={2} value={data.problem || ""} onChange={set("problem")} /></Field>
    <Field label="Solution, product, or service" full><Textarea rows={2} value={data.solution || ""} onChange={set("solution")} /></Field>
    <Field label="What makes this different?" full><Textarea rows={2} value={data.differentiator || ""} onChange={set("differentiator")} /></Field>
    <Field label="Traction, results, or accomplishments (optional)" full><Textarea rows={2} value={data.traction || ""} onChange={set("traction")} /></Field>
    <Field label="Funding request amount (optional)"><Input value={data.funding_amount || ""} onChange={set("funding_amount")} /></Field>
    <Field label="Use of funds (optional)" full><Textarea rows={2} value={data.use_of_funds || ""} onChange={set("use_of_funds")} /></Field>
    <Field label="Desired slide count / length"><Input value={data.slide_count || ""} onChange={set("slide_count")} /></Field>
    <Field label="Confidentiality requirements?" full><Textarea rows={2} value={data.confidentiality || ""} onChange={set("confidentiality")} /></Field>
  </IntakeGroup>
);

const WebsiteIntake = ({ set, data }) => (
  <IntakeGroup title="Website Intake">
    <Field label="Purpose of the website"><Input value={data.purpose || ""} onChange={set("purpose")} /></Field>
    <Field label="Approx. number of pages"><Input value={data.pages || ""} onChange={set("pages")} /></Field>
    <Field label="Existing domain / hosting"><Input value={data.domain || ""} onChange={set("domain")} /></Field>
    <Field label="Do you need copywriting?"><Input value={data.copywriting || ""} onChange={set("copywriting")} /></Field>
    <Field label="Style references or inspirations" full><Textarea rows={2} value={data.inspiration || ""} onChange={set("inspiration")} /></Field>
  </IntakeGroup>
);

const TrainingIntake = ({ set, data }) => (
  <IntakeGroup title="Training / Speaking Intake">
    <Field label="Event or program name"><Input value={data.event_name || ""} onChange={set("event_name")} /></Field>
    <Field label="Audience role & size"><Input value={data.audience || ""} onChange={set("audience")} /></Field>
    <Field label="Event date"><Input type="date" value={data.event_date || ""} onChange={set("event_date")} /></Field>
    <Field label="Session length"><Input value={data.length || ""} onChange={set("length")} /></Field>
    <Field label="Desired outcomes for participants" full><Textarea rows={2} value={data.outcomes || ""} onChange={set("outcomes")} /></Field>
  </IntakeGroup>
);

const AgentIntake = ({ set, data }) => (
  <IntakeGroup title="AI Agent Intake">
    <Field label="Role or workflow the agent should support" full><Textarea rows={2} value={data.role || ""} onChange={set("role")} /></Field>
    <Field label="Data sources involved"><Input value={data.data || ""} onChange={set("data")} /></Field>
    <Field label="Who will use / oversee the agent?"><Input value={data.users || ""} onChange={set("users")} /></Field>
    <Field label="Human review checkpoints required" full><Textarea rows={2} value={data.review || ""} onChange={set("review")} /></Field>
  </IntakeGroup>
);
